import { useState } from 'react';

interface Testimonial {
  id: number;
  author_name: string;
  text: string;
  source: string;
  approved: boolean;
}

interface TestimonialsEditorProps {
  data: Testimonial[];
  onChange: (data: Testimonial[]) => void;
}

export default function TestimonialsEditor({ data, onChange }: TestimonialsEditorProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Testimonial | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id);
    setEditForm({ ...testimonial });
    setShowAddForm(false);
  };

  const handleSave = () => {
    if (!editForm) return;
    const updatedData = data.map((t) => (t.id === editForm.id ? editForm : t));
    onChange(updatedData);
    setEditingId(null);
    setEditForm(null);
  };

  const handleDelete = (id: number) => {
    if (confirm('¿Estás seguro de eliminar este testimonio?')) {
      onChange(data.filter((t) => t.id !== id));
    }
  };

  const handleAdd = () => {
    const newTestimonial: Testimonial = {
      id: Math.max(...data.map((t) => t.id), 0) + 1,
      author_name: '',
      text: '',
      source: 'Google',
      approved: false,
    };
    setEditForm(newTestimonial);
    setShowAddForm(true);
    setEditingId(null);
  };

  const handleAddSave = () => {
    if (!editForm) return;
    onChange([...data, editForm]);
    setShowAddForm(false);
    setEditForm(null);
  };

  const toggleApproval = (id: number) => {
    const updatedData = data.map((t) =>
      t.id === id ? { ...t, approved: !t.approved } : t
    );
    onChange(updatedData);
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-chat-quote-line text-blue-600"></i>
            Gestionar Testimonios
          </h2>
          <p className="text-gray-600 mt-1">Aprobar, editar o eliminar testimonios</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Agregar Testimonio
        </button>
      </div>

      {(showAddForm || editingId !== null) && editForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {showAddForm ? 'Nuevo Testimonio' : 'Editar Testimonio'}
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Autor
                </label>
                <input
                  type="text"
                  value={editForm.author_name}
                  onChange={(e) => setEditForm({ ...editForm, author_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="María González"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fuente</label>
                <select
                  value={editForm.source}
                  onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Google">Google</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Yelp">Yelp</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Testimonio</label>
              <textarea
                value={editForm.text}
                onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Escribe el testimonio aquí..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="approved"
                checked={editForm.approved}
                onChange={(e) => setEditForm({ ...editForm, approved: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="approved" className="text-sm font-medium text-gray-700">
                Aprobado (visible en el sitio web)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={showAddForm ? handleAddSave : handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-save-line mr-2"></i>
                Guardar
              </button>
              <button
                onClick={() => {
                  setEditingId(null);
                  setEditForm(null);
                  setShowAddForm(false);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {data.map((testimonial) => (
          <div
            key={testimonial.id}
            className={`bg-white border rounded-lg p-4 ${
              testimonial.approved ? 'border-green-200 bg-green-50' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900">{testimonial.author_name}</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {testimonial.source}
                  </span>
                  {testimonial.approved && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                      <i className="ri-checkbox-circle-line"></i>
                      Aprobado
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm italic">"{testimonial.text}"</p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => toggleApproval(testimonial.id)}
                  className={`px-3 py-2 rounded-lg transition-colors whitespace-nowrap text-sm ${
                    testimonial.approved
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                  title={testimonial.approved ? 'Desaprobar' : 'Aprobar'}
                >
                  <i
                    className={testimonial.approved ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'}
                  ></i>
                </button>
                <button
                  onClick={() => handleEdit(testimonial)}
                  className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  title="Editar"
                >
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {data.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <i className="ri-chat-quote-line text-5xl text-gray-300 mb-4"></i>
          <p className="text-gray-500">No hay testimonios todavía</p>
        </div>
      )}
    </div>
  );
}
